import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase';

export default function AssessmentPage() {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    propertyAddress: '',
    city: '',
    email: '',
    principalResidence: '',
    propertyType: '',
    currentlyRented: '',
    reviewedRegulations: '',
    strataRestrictions: '',
    appliedForLicense: '',
    rentalType: '',
    startTimeline: '',
    primaryGoal: '',
    willingToComply: '',
    estimatedIncome: '',
    additionalNotes: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    // Validation
    if (!formData.propertyAddress || !formData.city || !formData.email) {
      alert('Please fill in all required fields');
      setIsSubmitting(false);
      return;
    }

    try {
      // Call Edge Function which saves to database AND sends email
      const { data, error } = await supabase.functions.invoke('send-assessment-email', {
        body: formData
      });

      if (error) throw error;

      // Navigate to eligibility results page with form data
      const params = new URLSearchParams();
      Object.entries(formData).forEach(([key, value]) => {
        if (value) params.set(key, value);
      });
      navigate(`/eligibility-results?${params.toString()}`);
    } catch (error) {
      console.error('Form submission error:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-4xl mx-auto px-6 py-6">
          <a href="/" className="inline-flex items-center text-gray-600 hover:text-gray-900 transition-colors">
            <i className="ri-arrow-left-line mr-2"></i>
            Back to Home
          </a>
        </div>
      </header>

      {/* Form Container */}
      <div className="max-w-4xl mx-auto px-6 py-12">
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">
            Property Compliance & Rental Strategy Assessment
          </h1>
          <p className="text-lg text-gray-600">
            Help us understand your property and goals so we can provide tailored compliance guidance
          </p>
        </div>

        <form 
          id="property-assessment-form"
          data-readdy-form
          onSubmit={handleSubmit} 
          className="bg-white border border-gray-200 rounded-lg p-8 shadow-sm"
        >
          {/* Section 1: Property Location */}
          <div className="mb-10">
            <h2 className="text-2xl font-bold text-gray-900 mb-6 pb-3 border-b border-gray-200">
              Section 1: Property Location
            </h2>
            
            <div className="space-y-6">
              <div>
                <label htmlFor="propertyAddress" className="block text-sm font-semibold text-gray-900 mb-2">
                  Property Address <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  id="propertyAddress"
                  name="propertyAddress"
                  value={formData.propertyAddress}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                  placeholder="123 Main Street, Unit 456"
                />
              </div>

              <div>
                <label htmlFor="city" className="block text-sm font-semibold text-gray-900 mb-2">
                  City <span className="text-red-500">*</span>
                </label>
                <select
                  id="city"
                  name="city"
                  value={formData.city}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm cursor-pointer"
                >
                  <option value="">Select a city</option>
                  <option value="Vancouver">Vancouver</option>
                  <option value="North Vancouver">North Vancouver</option>
                  <option value="Burnaby">Burnaby</option>
                  <option value="Surrey">Surrey</option>
                  <option value="Langley">Langley</option>
                  <option value="Victoria">Victoria</option>
                  <option value="Whistler">Whistler</option>
                  <option value="Calgary">Calgary</option>
                  <option value="Edmonton">Edmonton</option>
                  <option value="Toronto">Toronto</option>
                  <option value="Other">Other</option>
                </select>
              </div>

              <div>
                <label htmlFor="email" className="block text-sm font-semibold text-gray-900 mb-2">
                  Your Email <span className="text-red-500">*</span>
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                  placeholder="your@email.com"
                />
                <p className="text-xs text-gray-500 mt-1">We'll use this to send you your assessment results</p>
              </div>
            </div>
          </div>

          {/* Section 2: Ownership & Occupancy */}
          <div className="mb-10">
            <h2 className="text-2xl font-bold text-gray-900 mb-6 pb-3 border-b border-gray-200">
              Section 2: Ownership & Occupancy
            </h2>
            
            <div className="space-y-6">
              <div>
                <label className="block text-sm font-semibold text-gray-900 mb-3">
                  Do you live in this property as your principal residence?
                </label>
                <div className="space-y-2">
                  <label className="flex items-center cursor-pointer">
                    <input
                      type="radio"
                      name="principalResidence"
                      value="Yes"
                      checked={formData.principalResidence === 'Yes'}
                      onChange={handleChange}
                      className="w-4 h-4 text-teal-600 focus:ring-teal-500 cursor-pointer"
                    />
                    <span className="ml-3 text-sm text-gray-700">Yes</span>
                  </label>
                  <label className="flex items-center cursor-pointer">
                    <input
                      type="radio"
                      name="principalResidence"
                      value="No"
                      checked={formData.principalResidence === 'No'}
                      onChange={handleChange}
                      className="w-4 h-4 text-teal-600 focus:ring-teal-500 cursor-pointer"
                    />
                    <span className="ml-3 text-sm text-gray-700">No</span>
                  </label>
                  <label className="flex items-center cursor-pointer">
                    <input
                      type="radio"
                      name="principalResidence"
                      value="Part time"
                      checked={formData.principalResidence === 'Part time'}
                      onChange={handleChange}
                      className="w-4 h-4 text-teal-600 focus:ring-teal-500 cursor-pointer"
                    />
                    <span className="ml-3 text-sm text-gray-700">Part time</span>
                  </label>
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-900 mb-3">
                  Property Type
                </label>
                <div className="space-y-2">
                  {['Condo in strata', 'Townhouse', 'Detached home', 'Secondary suite', 'Laneway house', 'Other'].map((type) => (
                    <label key={type} className="flex items-center cursor-pointer">
                      <input
                        type="radio"
                        name="propertyType"
                        value={type}
                        checked={formData.propertyType === type}
                        onChange={handleChange}
                        className="w-4 h-4 text-teal-600 focus:ring-teal-500 cursor-pointer"
                      />
                      <span className="ml-3 text-sm text-gray-700">{type}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-900 mb-3">
                  Is it currently rented long term?
                </label>
                <div className="space-y-2">
                  {['Yes', 'No', 'Vacant'].map((status) => (
                    <label key={status} className="flex items-center cursor-pointer">
                      <input
                        type="radio"
                        name="currentlyRented"
                        value={status}
                        checked={formData.currentlyRented === status}
                        onChange={handleChange}
                        className="w-4 h-4 text-teal-600 focus:ring-teal-500 cursor-pointer"
                      />
                      <span className="ml-3 text-sm text-gray-700">{status}</span>
                    </label>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Section 3: Local Rule Awareness */}
          <div className="mb-10">
            <h2 className="text-2xl font-bold text-gray-900 mb-6 pb-3 border-b border-gray-200">
              Section 3: Local Rule Awareness
            </h2>
            
            <div className="space-y-6">
              <div>
                <label className="block text-sm font-semibold text-gray-900 mb-3">
                  Have you reviewed your city's short term rental regulations?
                </label>
                <div className="space-y-2">
                  {['Yes', 'No', 'Unsure'].map((answer) => (
                    <label key={answer} className="flex items-center cursor-pointer">
                      <input
                        type="radio"
                        name="reviewedRegulations"
                        value={answer}
                        checked={formData.reviewedRegulations === answer}
                        onChange={handleChange}
                        className="w-4 h-4 text-teal-600 focus:ring-teal-500 cursor-pointer"
                      />
                      <span className="ml-3 text-sm text-gray-700">{answer}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-900 mb-3">
                  Does your building or strata restrict short term rentals?
                </label>
                <div className="space-y-2">
                  {['Yes', 'No', 'Not applicable', 'Unsure'].map((answer) => (
                    <label key={answer} className="flex items-center cursor-pointer">
                      <input
                        type="radio"
                        name="strataRestrictions"
                        value={answer}
                        checked={formData.strataRestrictions === answer}
                        onChange={handleChange}
                        className="w-4 h-4 text-teal-600 focus:ring-teal-500 cursor-pointer"
                      />
                      <span className="ml-3 text-sm text-gray-700">{answer}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-900 mb-3">
                  Have you applied for any required business license or registration?
                </label>
                <div className="space-y-2">
                  {['Yes', 'No', 'Unsure'].map((answer) => (
                    <label key={answer} className="flex items-center cursor-pointer">
                      <input
                        type="radio"
                        name="appliedForLicense"
                        value={answer}
                        checked={formData.appliedForLicense === answer}
                        onChange={handleChange}
                        className="w-4 h-4 text-teal-600 focus:ring-teal-500 cursor-pointer"
                      />
                      <span className="ml-3 text-sm text-gray-700">{answer}</span>
                    </label>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Section 4: Rental Intent */}
          <div className="mb-10">
            <h2 className="text-2xl font-bold text-gray-900 mb-6 pb-3 border-b border-gray-200">
              Section 4: Rental Intent
            </h2>
            
            <div className="space-y-6">
              <div>
                <label className="block text-sm font-semibold text-gray-900 mb-3">
                  What type of rental are you considering?
                </label>
                <div className="space-y-2">
                  {['Short term under 30 days', 'Medium term 30 to 90 days', 'Long term only', 'Not sure'].map((type) => (
                    <label key={type} className="flex items-center cursor-pointer">
                      <input
                        type="radio"
                        name="rentalType"
                        value={type}
                        checked={formData.rentalType === type}
                        onChange={handleChange}
                        className="w-4 h-4 text-teal-600 focus:ring-teal-500 cursor-pointer"
                      />
                      <span className="ml-3 text-sm text-gray-700">{type}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-900 mb-3">
                  When would you like to start?
                </label>
                <div className="space-y-2">
                  {['Immediately', '1 to 3 months', 'Just researching'].map((timeline) => (
                    <label key={timeline} className="flex items-center cursor-pointer">
                      <input
                        type="radio"
                        name="startTimeline"
                        value={timeline}
                        checked={formData.startTimeline === timeline}
                        onChange={handleChange}
                        className="w-4 h-4 text-teal-600 focus:ring-teal-500 cursor-pointer"
                      />
                      <span className="ml-3 text-sm text-gray-700">{timeline}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-900 mb-3">
                  What is your primary goal?
                </label>
                <div className="space-y-2">
                  {['Maximize income', 'Cover mortgage', 'Travel and rent occasionally', 'Compliance review only'].map((goal) => (
                    <label key={goal} className="flex items-center cursor-pointer">
                      <input
                        type="radio"
                        name="primaryGoal"
                        value={goal}
                        checked={formData.primaryGoal === goal}
                        onChange={handleChange}
                        className="w-4 h-4 text-teal-600 focus:ring-teal-500 cursor-pointer"
                      />
                      <span className="ml-3 text-sm text-gray-700">{goal}</span>
                    </label>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Section 5: Risk & Alignment */}
          <div className="mb-10">
            <h2 className="text-2xl font-bold text-gray-900 mb-6 pb-3 border-b border-gray-200">
              Section 5: Risk & Alignment
            </h2>
            
            <div className="space-y-6">
              <div>
                <label className="block text-sm font-semibold text-gray-900 mb-3">
                  Are you willing to operate strictly within your city and provincial regulations?
                </label>
                <div className="space-y-2">
                  {['Yes', 'No', 'Unsure'].map((answer) => (
                    <label key={answer} className="flex items-center cursor-pointer">
                      <input
                        type="radio"
                        name="willingToComply"
                        value={answer}
                        checked={formData.willingToComply === answer}
                        onChange={handleChange}
                        className="w-4 h-4 text-teal-600 focus:ring-teal-500 cursor-pointer"
                      />
                      <span className="ml-3 text-sm text-gray-700">{answer}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div>
                <label htmlFor="estimatedIncome" className="block text-sm font-semibold text-gray-900 mb-2">
                  Estimated monthly income you hope to achieve
                </label>
                <input
                  type="text"
                  id="estimatedIncome"
                  name="estimatedIncome"
                  value={formData.estimatedIncome}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                  placeholder="e.g., $3,000 - $5,000"
                />
              </div>

              <div>
                <label htmlFor="additionalNotes" className="block text-sm font-semibold text-gray-900 mb-2">
                  Additional notes about your property
                </label>
                <textarea
                  id="additionalNotes"
                  name="additionalNotes"
                  value={formData.additionalNotes}
                  onChange={handleChange}
                  rows={4}
                  maxLength={500}
                  className="w-full px-4 py-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm resize-none"
                  placeholder="Any additional information that might help us assess your property..."
                />
                <p className="text-xs text-gray-500 mt-1">{formData.additionalNotes.length}/500 characters</p>
              </div>
            </div>
          </div>

          {/* Submit Button */}
          <div className="pt-6 border-t border-gray-200">
            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full bg-teal-600 text-white px-8 py-4 rounded-md font-semibold hover:bg-teal-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed whitespace-nowrap"
            >
              {isSubmitting ? 'Submitting...' : 'Review My Property Eligibility'}
            </button>

            {submitStatus === 'success' && (
              <div className="mt-4 p-4 bg-green-50 border border-green-200 rounded-md">
                <p className="text-green-800 text-sm font-medium">
                  <i className="ri-check-line mr-2"></i>
                  Thank you! We've received your assessment and will contact you shortly with personalized compliance guidance.
                </p>
              </div>
            )}

            {submitStatus === 'error' && (
              <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-md">
                <p className="text-red-800 text-sm font-medium">
                  <i className="ri-error-warning-line mr-2"></i>
                  Something went wrong. Please try again or contact us directly.
                </p>
              </div>
            )}
          </div>
        </form>

        {/* Contact Info */}
        <div className="mt-8 text-center text-sm text-gray-600">
          <p>Questions? Contact us at <a href="mailto:contact@furnishedrentalmanagement.com" className="text-teal-600 hover:text-teal-700">contact@furnishedrentalmanagement.com</a> or <a href="tel:+17783869229" className="text-teal-600 hover:text-teal-700">(778) 386-9229</a></p>
        </div>
      </div>
    </div>
  );
}
